import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface BannerSlide {
  key: string;
  /** Lower = more urgent: 0 something is stopped now … 5 informational. */
  priority: number;
  /** Renders the banner; `controls` goes next to its dismiss button (null when alone). */
  render: (controls: React.ReactNode) => React.ReactNode;
}

/** How long a slide stays before moving on (paused while hovered or focused). */
const AUTO_ADVANCE_MS = 5000;

/**
 * One banner slot for all top-of-app alerts (billing, Meta, WABA, sync).
 * The most urgent shows first; the others are behind "‹ 1/2 ›" inside the
 * same strip, and rotate every few seconds unless the user is on the banner.
 * So the screen never loses two or three strips to alerts.
 *
 * Each banner keeps its own colours and dismiss button. Dismissing removes it
 * from `slides` (the caller's state) and the next one takes its place.
 */
export function BannerCarousel({ slides }: { slides: BannerSlide[] }) {
  const ordered = [...slides].sort((a, b) => a.priority - b.priority);
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const [paused, setPaused] = React.useState(false);

  const count = ordered.length;
  const foundIndex = ordered.findIndex((s) => s.key === activeKey);
  const index = foundIndex >= 0 ? foundIndex : 0;
  const current = ordered[index];

  /*
   * The parent (AppShell) re-renders often — unread counts, sockets — and
   * hands over a NEW slides array each time. The timer must not restart on
   * those renders or it never fires, so it reads the latest order from a ref
   * and only resets when the visible slide, the count or the pause changes.
   */
  const orderRef = React.useRef<string[]>([]);
  orderRef.current = ordered.map((s) => s.key);
  const currentKey = current?.key ?? null;

  const go = React.useCallback(
    (step: number) => {
      const keys = orderRef.current;
      if (keys.length < 2) return;
      const at = Math.max(0, keys.indexOf(currentKey ?? ''));
      setActiveKey(keys[(at + step + keys.length) % keys.length]);
    },
    [currentKey],
  );

  React.useEffect(() => {
    if (count < 2 || paused) return;
    const timer = window.setTimeout(() => go(1), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [count, paused, currentKey, go]);

  // The strip unmounts while hovered when its last alert is closed, so no
  // mouseleave arrives — clear the pause so the next alerts still rotate.
  React.useEffect(() => {
    if (count === 0) setPaused(false);
  }, [count]);

  if (!current) return null;

  // Inherits the banner's text colour, so it reads on red, amber and white strips alike.
  const controls =
    count > 1 ? (
      <div
        className="flex shrink-0 items-center gap-0.5 rounded-full border border-current px-1 py-0.5 text-[11px] font-semibold leading-none opacity-90"
        role="group"
        aria-label="More alerts"
        data-testid="banner-carousel-controls"
      >
        <button
          type="button"
          onClick={() => go(-1)}
          className="rounded-full p-0.5 opacity-80 hover:opacity-100"
          aria-label="Previous alert"
          data-testid="banner-carousel-prev"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="min-w-[2.5rem] text-center tabular-nums" aria-live="polite">
          {index + 1} / {count}
        </span>
        <button
          type="button"
          onClick={() => go(1)}
          className="rounded-full p-0.5 opacity-80 hover:opacity-100"
          aria-label="Next alert"
          data-testid="banner-carousel-next"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    ) : null;

  return (
    <div
      data-testid="banner-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div key={current.key} className="animate-in fade-in duration-300">
        {current.render(controls)}
      </div>
    </div>
  );
}
